import { Button, Checkbox, CustomSelect, SelectOption, TextInput } from '@consigli/facade';
import { useProject, useProjectId, usePackageId, useUpdateBlobMutation } from '@consigli/hooks';
import { Country, FoldersDocumentType, NS3451, OMUK } from '@consigli/types';
import { ChangeEvent, FC, useState } from 'react';
import { Controller, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiOutlineXMark } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import Popup from 'reactjs-popup';

import { FolderDocumentsType } from '@/util/types';

import { FolderStructureStandard, mappingDict } from '../util/foldername-mappings';

interface EditOperationsFilesPopupProps {
  isMultiple: boolean;
  documents: FolderDocumentsType[];
  onClose: () => void;
}

type UpdateOperationsFilesForm = {
  filename?: string;
  folderStructure: FolderStructureStandard[];
  documentType: FoldersDocumentType[];
  moveFile?: boolean;
};

export const EditOperationsFilesPopup: FC<EditOperationsFilesPopupProps> = ({
  isMultiple,
  documents,
  onClose,
}) => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { t } = useTranslation();
  const [updateBlob] = useUpdateBlobMutation();
  const { project } = useProject(projectId);

  const isUK = project?.country === Country.UNITED_KINGDOM;
  const standardEnum = isUK ? OMUK : NS3451;
  const standardKey = (isUK ? 'OMUK' : 'ns3451') as keyof typeof mappingDict;

  const standardOptions = Object.entries(standardEnum).map(([key, value]) => ({
    label: (mappingDict[standardKey] as Record<string, string>)[key] ?? key,
    value,
  }));
  const sharedFolderStructure = standardOptions
    .filter((option) =>
      documents.every((document) => document.folderStructure.includes(option.value)),
    )
    .map((option) => option.value as FolderStructureStandard);

  const documentTypeOptions = Object.entries(FoldersDocumentType).map(([key, value]) => ({
    label: t('folders.document-type.' + key),
    value,
  }));

  const sharedDocumentTypes = documentTypeOptions
    .filter((option) => documents.every((document) => document.documentType.includes(option.value)))
    .map((option) => option.value);

  const { register, handleSubmit, control, getValues } = useForm<UpdateOperationsFilesForm>();
  const {
    field: { value: selectedFolderStructure, onChange: folderStructureOnChange },
  } = useController({
    name: 'folderStructure',
    control,
    defaultValue: sharedFolderStructure,
  });
  const {
    field: { value: selectedDocumentTypes, onChange: documentTypesOnChange },
  } = useController({
    name: 'documentType',
    control,
    defaultValue: sharedDocumentTypes,
  });
  const [moveFile, setMoveFile] = useState<boolean>(getValues('moveFile') ?? false);

  const onSubmit = async (data: UpdateOperationsFilesForm) => {
    try {
      await Promise.all(
        documents.map((document) => {
          // Determine the folder structure values and document types to add and remove
          // This needs to be done because patch overwrites the current list in  the db,
          // and we need to support both updating single and multiple files
          let updatedFolderStructure: FolderStructureStandard[] = [];
          let updatedDocumentTypes: FoldersDocumentType[] = [];
          if (data.moveFile) {
            updatedFolderStructure =
              data.folderStructure.length > 0
                ? data.folderStructure
                : [standardEnum.UNCATEGORIZED as FolderStructureStandard];
            updatedDocumentTypes = data.documentType;
          } else {
            const folderStructureToAdd = data.folderStructure.filter(
              (value) => !document.folderStructure.includes(value),
            );
            const folderStructureToRemove = sharedFolderStructure.filter(
              (value) => !data.folderStructure.includes(value),
            );
            updatedFolderStructure = document.folderStructure
              .filter((value) => !folderStructureToRemove.includes(value))
              .concat(folderStructureToAdd);
            updatedFolderStructure =
              updatedFolderStructure.length > 0
                ? updatedFolderStructure
                : [standardEnum.UNCATEGORIZED as FolderStructureStandard];
            const documentTypeToAdd = data.documentType.filter(
              (value) => !document.documentType.includes(value),
            );
            const documentTypeToRemove = sharedDocumentTypes.filter(
              (value) => !data.documentType.includes(value),
            );
            updatedDocumentTypes = document.documentType
              .filter((value) => !documentTypeToRemove.includes(value))
              .concat(documentTypeToAdd);
          }

          return updateBlob({
            projectId,
            packageId,
            blobId: document.id,
            data: {
              name: data.filename,
              folderStructure: updatedFolderStructure,
              documentType: updatedDocumentTypes,
            },
          });
        }),
      );
      toast.success(t('folders.update-success'));
      onClose();
    } catch (error) {
      console.log('Error updating files:', error);
      toast.error(t('folders.update-error'));
    }
  };

  return (
    <Popup
      overlayStyle={{ background: 'rgba(100, 100, 100, 0.35)' }}
      contentStyle={{
        backgroundColor: '#fbfcfd',
        borderRadius: '10px',
        width: '80%',
        maxWidth: '500px',
      }}
      modal
      open
      onClose={onClose}
    >
      <div className="flex flex-col flex-nowrap gap-y-4 p-4">
        <div className="flex flex-row justify-between">
          <h2 className="font-bold text-2xl text-day-neutral-dark">{t('folders.edit-metadata')}</h2>
          <Button className="p-0" tertiary icon={HiOutlineXMark} onClick={onClose} iconSize={25} />
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-2 bg-day-light-4">
            {!isMultiple && (
              <>
                <p>{t('folders.edit-filename')}</p>
                <TextInput
                  className="w-full mb-6"
                  {...register('filename')}
                  defaultValue={documents[0].name}
                />
              </>
            )}
            <p>{isUK ? t('folders.omuk') : t('folders.ns3451')}</p>
            <CustomSelect
              placeholder={isUK ? t('folders.select-omuk') : t('folders.select-ns3451')}
              isMulti
              value={
                selectedFolderStructure != null
                  ? standardOptions.filter((option) =>
                      selectedFolderStructure.includes(option.value),
                    )
                  : []
              }
              options={standardOptions}
              onChange={(options: SelectOption[]) =>
                folderStructureOnChange(options.map((option) => option.value))
              }
              noOptionsMessage={t('custom-select.no-options')}
            />
            <p className="mt-4">{t('folders.document-types')}</p>
            <CustomSelect
              placeholder={t('folders.select-document-types')}
              isMulti
              isClearable={false}
              value={
                selectedDocumentTypes != null
                  ? documentTypeOptions.filter((option) =>
                      selectedDocumentTypes.includes(option.value),
                    )
                  : []
              }
              options={documentTypeOptions}
              onChange={(options: SelectOption[]) =>
                documentTypesOnChange(options.map((option) => option.value))
              }
              noOptionsMessage={t('custom-select.no-options')}
            />
            <div className="mt-4">
              <Controller
                name="moveFile"
                control={control}
                render={({ field }) => (
                  <div className="flex">
                    <Checkbox
                      id="moveFile"
                      label={t('folders.move-file')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        field.onChange(event.target.checked);
                        setMoveFile(event.target.checked);
                      }}
                      value={moveFile?.toString()}
                      checked={moveFile}
                    ></Checkbox>
                  </div>
                )}
              />
            </div>
            <div className="flex gap-4 pt-4">
              <Button className="flex-1" primary rounded type="submit">
                {t('folders.update')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Popup>
  );
};
